import type { ExpiredUrlResponse, ExpiredUrlType } from './entities/article';

const INVALID_INPUT_RESPONSE: ExpiredUrlResponse = {
  data: null,
  message: 'invalid-expired-url-input',
  status: 'success',
};

export function normalizeExpiredUrlSlug(type: ExpiredUrlType, slug: string): string {
  let result = slug.trim();
  for (let depth = 0; depth < 4 && result.length > 0; depth++) {
    if (type === 'topic' && result.startsWith('topic/topic/')) {
      result = result.slice('topic/'.length);
      continue;
    }
    if (type === 'article' && result.startsWith('article/article/')) {
      result = result.slice('article/'.length);
      continue;
    }
    if (type === 'author' && result.startsWith('author/author/')) {
      result = result.slice('author/'.length);
      continue;
    }
    break;
  }
  return result;
}

export function getExpiredUrlSkipReason(type: ExpiredUrlType, slug: string): string | null {
  const normalized = normalizeExpiredUrlSlug(type, slug);
  if (!normalized) return 'empty_slug';
  if (normalized.length > 512) return 'slug_too_long';
  const lower = normalized.toLowerCase();
  if (lower.includes('http://') || lower.includes('https://')) {
    return 'absolute_url';
  }
  if (normalized.includes('..')) {
    return 'path_traversal';
  }

  if (type === 'topic') {
    return /^[a-zA-Z0-9][a-zA-Z0-9/_-]{0,240}$/.test(normalized) ? null : 'invalid_topic_slug';
  }
  if (type === 'article') {
    return /^[a-zA-Z0-9][a-zA-Z0-9/_-]{0,240}$/.test(normalized) ? null : 'invalid_article_slug';
  }
  if (type === 'author') {
    return /^author\/[a-zA-Z0-9][a-zA-Z0-9/_-]{0,200}$/.test(normalized) ? null : 'invalid_author_slug';
  }
  if (type === 'quote') {
    return /^[a-zA-Z0-9][a-zA-Z0-9./_-]{0,240}$/.test(normalized) ? null : 'invalid_quote_slug';
  }
  return 'invalid_type';
}

export function skipExpiredUrlFetchResponse(): ExpiredUrlResponse {
  return INVALID_INPUT_RESPONSE;
}

const isExpiredUrlNotFoundMessage = (response: Pick<ExpiredUrlResponse, 'data' | 'message'>): boolean =>
  response.data === null && response.message === 'Expired URL not found';

/**
 * Interim normalization until editorial-tools returns HTTP 200 with
 * `{ status: "success", is_expired: boolean, data: ... }` for all non-5xx responses.
 * Today many "not expired" responses use `status: "error"` in the JSON body (still HTTP 200/404).
 */
export function normalizeExpiredUrlApiResponse(response: ExpiredUrlResponse): ExpiredUrlResponse {
  if (!isExpiredUrlNotFoundMessage(response)) {
    return response;
  }
  return {
    data: null,
    is_expired: false,
    message: response.message,
    status: 'success',
  };
}

/** True when the URL should be treated as expired (410 / hidden term). */
export function isExpiredUrlActive(response: ExpiredUrlResponse | undefined): boolean {
  if (!response) return false;
  if (typeof response.is_expired === 'boolean') {
    return response.is_expired;
  }
  return response.status === 'success' && Boolean(response.data);
}
